/* Created by Interactive 63 - https://interactive63.com */
/**
 * Thin stand-ins for the Export dialog and the presets panel. Each loads its real module the
 * first time it is opened, which keeps syntax highlighting, zip building and the preset library
 * out of the initial download.
 */
import type { Store } from '../store/store';
import { toast } from '../ui/toast';
import type { ExportDialog } from './export-dialog';
import type { PresetsPanel, PresetsPanelDeps } from './presets-panel';
import type { SceneSettingsDialog } from './scene-settings';

export interface OpenableDialog {
  open(): void;
}

export interface PresetsOpener {
  open(anchor: HTMLElement): void;
}

/** Runs a loader, telling the user if the module could not be fetched. */
function loadOrWarn(task: () => Promise<void>): void {
  task().catch(() => {
    toast.error("That couldn't load. Check your connection and try again.");
  });
}

/** An Export dialog that is created on first use. */
export function lazyExportDialog(store: Store, scene: SceneSettingsDialog): OpenableDialog {
  let dialog: ExportDialog | undefined;
  return {
    open: () => {
      loadOrWarn(async () => {
        if (!dialog) {
          const { ExportDialog: Created } = await import('./export-dialog');
          dialog ??= new Created(store, scene);
        }
        dialog.open();
      });
    },
  };
}

/** A presets panel that is created on first use. */
export function lazyPresetsPanel(store: Store, deps: PresetsPanelDeps): PresetsOpener {
  let panel: PresetsPanel | undefined;
  return {
    open: (anchor) => {
      loadOrWarn(async () => {
        if (!panel) {
          const { PresetsPanel: Created } = await import('./presets-panel');
          panel ??= new Created(store, deps);
        }
        panel.open(anchor);
      });
    },
  };
}
